// import components
import { Tooltip } from '@mui/material';
import { RosterDataGrid } from 'components/shared/RosterViewDataGrid';
import { WeekValueCell } from 'components/shared/RosterViewDataGrid/components/cells/WeekValueCell';
import OverflowTooltip from 'components/shared/OverflowTooltip';
import BacklogEntityActionsMenu from '../BacklogEntityActionsMenu';
import BacklogCategoryLabel from '../BacklogCategoryLabel';

// import external libraries
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import utils & constants
import {
  isEventCategory,
  ROW_HEIGHT,
  TIMELINE_COLUMN_WIDTH
} from './BacklogGrid.utils';
import {
  formatEntityDisplayLabel,
  getIconType
} from 'components/mrp/mrp.utils';

// import types
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowClassNameParams
} from '@mui/x-data-grid-pro';
import type { BacklogRow } from '../../Backlog.types';
import type { BacklogGridProps } from './BacklogGrid.types';

// import styles
import {
  CATEGORY_ROW_CLASS,
  CARE_RECEIVER_ROW_CLASS,
  TOTAL_COLUMN_CLASS,
  EntityIcon,
  CareReceiverNameGroup,
  CategoryLabelCell,
  dataGridSx,
  HeaderLabel,
  LabelCell,
  OpenWeekCell,
  RuleLabelCell,
  RuleNoteIcon
} from './BacklogGrid.styles';

const BacklogGrid = ({
  rows,
  rosterDays,
  cellValueMode,
  showTotals,
  onToggleCategoryRow,
  onOpenWeek
}: BacklogGridProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'backlog' });

  // The cell speaks of current/total, the page of planned/prescribed — same pair, mapped once here.
  const cellDisplay =
    cellValueMode === 'plannedOverPrescribed' ? 'ratio' : 'difference';

  const renderTimelineCell = useCallback(
    (params: GridRenderCellParams<BacklogRow>) => {
      // The care receiver row is a band across the width; only categories and rules carry numbers.
      if (params.row.rowType === 'careReceiver') return null;

      const cell = (
        <WeekValueCell
          value={params.value}
          // A category row totals the rules under it, so it reports rather than flags — neutral and
          // unsigned. Outside treatment is such a row.
          isSummary={params.row.rowType === 'category'}
          display={cellDisplay}
        />
      );

      // An event-category week with appointments opens that week's detail; an empty one has nothing
      // to open, so it is left inert.
      if (isEventCategory(params.row.category) && params.value != null) {
        return (
          <OpenWeekCell
            onClick={() => onOpenWeek(params.field)}
            // Announced as a button, so it must also be reachable and activatable by keyboard.
            onKeyDown={(keyEvent) => {
              if (keyEvent.key !== 'Enter' && keyEvent.key !== ' ') return;
              keyEvent.preventDefault();
              onOpenWeek(params.field);
            }}
            role="button"
            tabIndex={0}
            aria-label={t('openWeek')}
          >
            {cell}
          </OpenWeekCell>
        );
      }

      return cell;
    },
    [cellDisplay, onOpenWeek, t]
  );

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<BacklogRow>) => {
      if (params.row.rowType === 'careReceiver') return CARE_RECEIVER_ROW_CLASS;
      if (params.row.rowType === 'category') return CATEGORY_ROW_CLASS;
      return '';
    },
    []
  );

  const fixedColumnsLeft: GridColDef<BacklogRow>[] = useMemo(
    () => [
      {
        field: 'activityName',
        // Deliberately blank rather than "Activiteit": this column carries three kinds of row — a care
        // receiver, a task category, and only then the activity of a rule — so naming it after the last
        // of those would mislabel the two above it. Empty rather than omitted, since the grid falls back
        // to the field name.
        headerName: '',
        width: 300,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const { row } = params;

          if (row.rowType === 'careReceiver') {
            return (
              <LabelCell>
                <CareReceiverNameGroup>
                  {/* The planning-entity symbol, per the row's own kind — client, employee, resource
                      or workplace: the glyph via getIconType, the colour via the entityType prop. */}
                  <EntityIcon
                    type={getIconType(row.entityType)}
                    entityType={row.entityType}
                  />
                  <HeaderLabel variant="body1" noWrap>
                    {formatEntityDisplayLabel(row.entityName, row.entityNumber)}
                  </HeaderLabel>
                </CareReceiverNameGroup>
                <BacklogEntityActionsMenu
                  entityId={row.entityId}
                  entityName={row.entityName}
                  entityType={row.entityType}
                />
              </LabelCell>
            );
          }

          if (row.rowType === 'category') {
            const label = t(`categories.${row.category}`);
            // Event categories do not fold — their cells open the week instead — so they show no chevron.
            const isFoldable = !isEventCategory(row.category);
            return (
              <CategoryLabelCell>
                <BacklogCategoryLabel
                  label={label}
                  count={row.taskCount ?? 0}
                  fold={
                    isFoldable
                      ? {
                          isExpanded: !!row.isExpanded,
                          onToggle: () =>
                            onToggleCategoryRow(row.entityId, row.category),
                          ariaLabel: t('toggleCategory', { name: label })
                        }
                      : undefined
                  }
                />
              </CategoryLabelCell>
            );
          }

          return (
            <RuleLabelCell>
              <OverflowTooltip variant="body2">
                {row.activityName}
              </OverflowTooltip>
              {row.description && (
                <Tooltip title={row.description} arrow>
                  <RuleNoteIcon type="note" color="action" />
                </Tooltip>
              )}
            </RuleLabelCell>
          );
        }
      }
    ],
    [t, onToggleCategoryRow]
  );

  /**
   * Empty rather than hidden when the totals are switched off: the grid pins columns by field name, so a
   * pinned field that no longer exists would be pinning nothing. The pinning below drops with it.
   */
  const fixedColumnsRight: GridColDef<BacklogRow>[] = useMemo(
    () =>
      !showTotals
        ? []
        : [
            {
              field: 'totalDifference',
              headerName: t('columns.total'),
              // Flexes with the week columns (which also flex) so the Totaal column reads the same
              // width as them, rather than staying pinned to a narrower fixed width. The requested
              // width becomes its floor, matching the week columns' minWidth.
              flex: 1,
              minWidth: TIMELINE_COLUMN_WIDTH,
              sortable: false,
              disableColumnMenu: true,
              align: 'center',
              headerAlign: 'center',
              // A vertical divider before the pinned Totaal column, styled via dataGridSx.
              headerClassName: TOTAL_COLUMN_CLASS,
              cellClassName: TOTAL_COLUMN_CLASS,
              // Fed the same shape as a week cell so the totals badge matches the week badges. Left empty
              // on the care receiver band, which carries no numbers of its own.
              renderCell: (params) => {
                if (params.row.rowType === 'careReceiver') return null;

                // A category row totals the work outstanding across its weeks, which is already a magnitude
                // — there is no planned/prescribed pair to subtract for it. Outside treatment is such a
                // row: its total is a count of stray appointments, carried on `outstanding`.
                if (params.row.rowType === 'category') {
                  return (
                    <WeekValueCell value={params.row.outstanding} isSummary />
                  );
                }

                return (
                  <WeekValueCell
                    value={{
                      current: params.row.totalPlanned,
                      total: params.row.totalPrescribed,
                      cellColSpan: 1
                    }}
                    display={cellDisplay}
                  />
                );
              }
            }
          ],
    [t, cellDisplay, showTotals]
  );

  return (
    <RosterDataGrid<BacklogRow>
      rosterDays={rosterDays}
      rows={rows}
      fixedColumnsLeft={fixedColumnsLeft}
      fixedColumnsRight={fixedColumnsRight}
      timelineView="weeksOnly"
      pinnedColumns={{
        left: ['activityName'],
        right: showTotals ? ['totalDifference'] : []
      }}
      renderTimelineCell={renderTimelineCell}
      getRowClassName={getRowClassName}
      rowHeight={ROW_HEIGHT}
      timelineColumnWidth={TIMELINE_COLUMN_WIDTH}
      // This is a full page table, so the eight weeks share the width the label and total columns leave
      // over rather than stopping short of the edge. It also closes the blank strip that appeared
      // between the last week and the pinned total, which was the grid's filler for the unused width.
      stretchTimelineColumns
      sx={dataGridSx}
    />
  );
};

export default memo(BacklogGrid);
